import { memo, useEffect, useMemo } from 'react'
import {
  checkInputErrorEmail,
  checkInputErrorFileMaxSize,
  checkInputErrorMaxLength,
  checkInputErrorMinLength,
  checkInputErrorOnlyNumbers,
  checkInputErrorRequired,
  formatDate,
  useFile,
  useInput,
  useRadio,
  useSelect,
  useTextarea
} from '@chilecompra/react-kit'
import { Button, Card, File, Input, Select, Textarea, Toast, Typography } from '@chilecompra/react-kit/components'
import PropTypes from 'prop-types'

import { ACCEPTED_DOCUMENT_TYPES, DEFAULT_PHONE_ID, MAX_MB } from '../../configs/settings/constants'
import { isLocalEnv } from '../../configs/settings/environment'
import { useAuthProvider } from '../AuthProvider/AuthProvider.hook'
import { Container, Files, Item, PhoneContainer, PhoneField, Radio, RemoveIcon } from './RequestDetailInfo.styles'

/**
 * The RequestDetailInfo's addResponse.
 */
const AddResponseForm = props => {
  const {
    countries,
    errorCountries,
    formType,
    isError,
    isLoading,
    isResponseMultipleOptions,
    loadingCountries,
    multipleChoiceOptions,
    onAdd,
    onRemove,
    onResponse,
    publicUserDni,
    publicUserName,
    questionId,
    responseUser,
    tempResponseAttachedFiles
  } = props

  const { isPrivate } = useAuthProvider()

  const {
    error: nameError,
    onChange: handleNameChange,
    value: nameValue
  } = useInput({
    errorCallbacks: [checkInputErrorRequired('Por favor ingrese un nombre o razón social')],
    overwriteCallback: () =>
      formType === 'public_national' ? publicUserName : isLocalEnv ? 'Usuario Local Extranjero' : ''
  })

  const {
    error: emailError,
    onChange: handleEmailChange,
    value: emailValue
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired('Por favor ingrese un correo electrónico'),
      checkInputErrorEmail('El formato del correo electrónico es incorrecto'),
      checkInputErrorMaxLength(100, 'El correo electrónico no puede exceder los 100 caracteres')
    ],
    initialValue: isLocalEnv ? 'test@test.com' : ''
  })

  const {
    onChange: onPhoneCodeChange,
    setValue: setPhoneCodeValue,
    value: phoneCodeValue
  } = useSelect({
    initialValue: DEFAULT_PHONE_ID
  })

  const {
    error: phoneError,
    value: phoneValue,
    onChange: handlePhoneChange,
    onReset: handlePhoneReset
  } = useInput({
    errorCallbacks: [
      checkInputErrorOnlyNumbers('El número de teléfono debe contener solo números'),
      checkInputErrorMinLength(8, 'El número de teléfono debe tener al menos 8 dígitos'),
      checkInputErrorMaxLength(15, 'El número de teléfono no puede exceder los 15 dígitos')
    ],
    overwriteCallback: updatedValue => {
      if (updatedValue) return

      handlePhoneReset()
    }
  })

  const {
    error: fileError,
    onChange: handleFileChange,
    onReset: handleFileReset,
    setValue: setFileValue,
    value: fileValue
  } = useFile({
    errorCallbacks: [checkInputErrorFileMaxSize(MAX_MB)]
  })

  const {
    error: messageError,
    onChange: handleMessageChange,
    value: messageValue
  } = useTextarea({
    errorCallbacks: [
      checkInputErrorRequired('Este campo es obligatorio. Debes completar esta información para poder continuar.'),
      checkInputErrorMaxLength(500)
    ],
    initialValue: isLocalEnv ? 'Mensaje de prueba' : ''
  })

  const { onChange: handleMultipleChoiceOptionChange, value: multipleChoiceOptionValue } = useRadio()

  const countryPhoneCodeOptions = useMemo(() => {
    return countries
      .map(option => ({
        name: option.name,
        value: option.id
      }))
      .filter(option => {
        if (formType === 'public_foreign') {
          setPhoneCodeValue('4')
          return option.value !== Number(DEFAULT_PHONE_ID)
        }

        return option.value === Number(DEFAULT_PHONE_ID)
      })
  }, [countries, formType])

  const handleResponse = () => {
    onResponse({
      dni: formType === 'public_national' ? publicUserDni : '',
      email: emailValue,
      isForeign: formType === 'public_foreign',
      isPrivate,
      message: messageValue || multipleChoiceOptionValue,
      name: nameValue,
      phone: phoneValue,
      phoneCode: phoneValue ? phoneCodeValue : '',
      questionId,
      tempResponseAttachedFiles
    })
  }

  useEffect(() => {
    if (fileError !== '') return setFileValue('')

    if (fileValue)
      onAdd({
        file: fileValue,
        questionId
      })
  }, [fileError, fileValue])

  useEffect(() => {
    if (tempResponseAttachedFiles.length === 0) handleFileReset()
  }, [tempResponseAttachedFiles])

  const isSubmitDisabled = useMemo(() => {
    const hasFormError =
      messageError !== '' || (!isPrivate && (nameError !== '' || emailError !== '' || phoneError !== ''))

    const hasFormIncomplete =
      (!messageValue && !multipleChoiceOptionValue) || (!isPrivate && (!nameValue || !emailValue))

    return hasFormError || hasFormIncomplete
  }, [
    emailError,
    emailValue,
    messageError,
    messageValue,
    multipleChoiceOptionValue,
    nameError,
    nameValue,
    phoneError,
    isPrivate
  ])

  if (!isPrivate && !publicUserDni && formType === 'public_national') return null

  return (
    <Container spacing={2}>
      {!isPrivate && (
        <>
          <Item xs={12}>
            <Input
              disabled={isLoading || formType === 'public_national'}
              error={nameError}
              label="Nombre"
              onChange={handleNameChange}
              placeholder="Ingresa tu nombre o razón social"
              value={nameValue}
            />
          </Item>
          <Item lg={6} xs={12}>
            <Input
              disabled={isLoading}
              error={emailError}
              label="Correo electrónico"
              maxLength={100}
              onChange={handleEmailChange}
              placeholder="Ingresa tu correo electrónico"
              value={emailValue}
            />
          </Item>
          <Item lg={6} xs={12}>
            <PhoneContainer>
              <Typography
                margin="0 0 0.5rem"
                style={{
                  fontWeight: 500
                }}
                variant="caption"
              >
                Número de teléfono (opcional)
              </Typography>

              <PhoneField>
                <Select
                  disabled={!!errorCountries || isLoading || loadingCountries || formType === 'public_national'}
                  error={errorCountries ? 'Error al obtener los países' : ''}
                  onChange={onPhoneCodeChange}
                  options={countryPhoneCodeOptions}
                  placeholder="Selecciona el código de país"
                  value={phoneCodeValue}
                />

                <Input
                  disabled={!!errorCountries || isLoading || loadingCountries}
                  error={phoneValue ? phoneError : ''}
                  maxLength={15}
                  onChange={handlePhoneChange}
                  placeholder="Ingresa tu número de teléfono"
                  value={phoneValue}
                />
              </PhoneField>
            </PhoneContainer>
          </Item>
        </>
      )}

      {!isResponseMultipleOptions && (
        <Item xs={12}>
          <File
            accept={ACCEPTED_DOCUMENT_TYPES}
            browseText={fileValue ? 'Agregar otro archivo' : 'Examinar'}
            disabled={isLoading}
            error={fileError}
            info={`Tamaño máximo permitido: ${MAX_MB}MB.`}
            label={
              <Typography
                margin="0 0 0.18rem"
                style={{
                  fontWeight: 500
                }}
                variant="caption"
              >
                Documento anexo (Opcional)
              </Typography>
            }
            margin="0 0 16px"
            onChange={handleFileChange}
            placeholder="Presiona examinar para adjuntar un archivo."
            value={fileValue}
          />

          {tempResponseAttachedFiles.length > 0 && (
            <Container margin="0 0 8px">
              <Typography
                margin="0 0 16px"
                style={{
                  fontWeight: 500
                }}
                variant="body2"
              >
                Archivos adjuntos:
              </Typography>

              {tempResponseAttachedFiles.map(file => (
                <Card key={file.id} margin="0 0 8px" padding="24px">
                  <Files>
                    <Typography className="file__name" variant="body2">
                      {file.fileName.toLowerCase()}
                    </Typography>

                    <RemoveIcon
                      disabled={isLoading}
                      onClick={() =>
                        onRemove({
                          fileId: file.id,
                          questionId
                        })
                      }
                    />
                  </Files>
                </Card>
              ))}
            </Container>
          )}
        </Item>
      )}

      {multipleChoiceOptions.length === 0 && (
        <Item xs={12}>
          <Textarea
            disabled={isLoading}
            error={messageError}
            label="Escribe tu respuesta"
            margin="0 0 8px"
            maxCount={500}
            onChange={handleMessageChange}
            rows={4}
            value={messageValue}
          />
        </Item>
      )}

      {multipleChoiceOptions.length !== 0 && (
        <Item xs={12}>
          <Radio
            disabled={isResponseMultipleOptions || isLoading}
            onChange={handleMultipleChoiceOptionChange}
            options={multipleChoiceOptions.map(option => ({
              label: option.message,
              value: option.message
            }))}
            value={multipleChoiceOptionValue || (responseUser?.message ? responseUser?.message : 0)}
          />
        </Item>
      )}

      <Item xs={12}>
        <Container alignItems="center" justifyContent={isResponseMultipleOptions ? 'flex-start' : 'flex-end'}>
          <Button color="primary" disabled={isSubmitDisabled || isLoading} loading={isLoading} onClick={handleResponse}>
            Responder
          </Button>
        </Container>
      </Item>

      {isError && (
        <Item xs={12}>
          <Toast
            border
            margin="16px 0 0"
            severity="error"
            textDirection="vertical"
            title="Lo sentimos, estamos presentando problemas con nuestro servidor."
          >
            En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
            más.
          </Toast>
        </Item>
      )}

      {isResponseMultipleOptions && (
        <Item>
          <Toast margin="16px 0 0" severity="info">
            Ya respondiste esta pregunta con fecha{' '}
            {responseUser &&
              formatDate(
                responseUser.registerDate,
                `dd/MM/yyyy 'a las' ${formatDate(responseUser.registerDate, 'HH:mm')}`
              )}
          </Toast>
        </Item>
      )}
    </Container>
  )
}

AddResponseForm.defaultProps = {
  countries: [],
  errorCountries: null,
  isError: false,
  isLoading: false,
  isResponseMultipleOptions: false,
  loadingCountries: false,
  multipleChoiceOptions: [],
  responseUser: undefined
}

AddResponseForm.propTypes = {
  countries: PropTypes.array,
  errorCountries: PropTypes.any,
  formType: PropTypes.string.isRequired,
  isError: PropTypes.bool,
  isLoading: PropTypes.bool,
  isResponseMultipleOptions: PropTypes.bool,
  loadingCountries: PropTypes.bool,
  multipleChoiceOptions: PropTypes.array,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onResponse: PropTypes.func.isRequired,
  publicUserDni: PropTypes.string.isRequired,
  publicUserName: PropTypes.string.isRequired,
  questionId: PropTypes.string.isRequired,
  responseUser: PropTypes.shape(),
  tempResponseAttachedFiles: PropTypes.arrayOf(PropTypes.shape())
}

export default memo(AddResponseForm)
